/**
 * Role-based theming utility for consistent colors across Reader and Vendor experiences
 *
 * Reader: Indigo color scheme (indigo-500 base / indigo-600 hover)
 * Vendor: Purple color scheme (purple-600 base / purple-700 hover)
 */

export type UserRole = 'reader' | 'vendor'
export interface RoleThemeColors {
  // Primary colors
  primary: string
  primaryHover: string
  primaryLight: string
  primaryDark: string

  // Background colors
  bgPrimary: string
  bgPrimaryHover: string
  bgPrimaryLight: string

  // Text colors
  textPrimary: string
  textPrimaryHover: string

  // Border colors
  borderPrimary: string

  // Focus ring
  focusRing: string
  focusBorder: string

  // Active/Selected states
  activeBackground: string
  activeText: string

  // Button variants
  buttonPrimary: string
  buttonPrimaryHover: string
  buttonSecondary: string
  buttonSecondaryHover: string

  // Form elements
  inputFocus: string
  checkboxChecked: string
  toggleChecked: string
  toggleFocusRing: string
}
const readerTheme: RoleThemeColors = {
  // Primary colors
  primary: 'indigo-500',
  primaryHover: 'indigo-600',
  primaryLight: 'indigo-100',
  primaryDark: 'indigo-700',
  // Background colors
  bgPrimary: 'bg-indigo-500',
  bgPrimaryHover: 'hover:bg-indigo-600',
  bgPrimaryLight: 'bg-indigo-100',
  // Text colors
  textPrimary: 'text-indigo-500',
  textPrimaryHover: 'hover:text-indigo-600',
  // Border colors
  borderPrimary: 'border-indigo-500',
  // Focus ring
  focusRing: 'focus:ring-indigo-500',
  focusBorder: 'focus:border-indigo-500',
  // Active/Selected states
  activeBackground: 'bg-indigo-100',
  activeText: 'text-indigo-600',
  // Button variants
  buttonPrimary: 'bg-indigo-500 hover:bg-indigo-600 text-white',
  buttonPrimaryHover: 'hover:bg-indigo-600',
  buttonSecondary: 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200',
  buttonSecondaryHover: 'hover:bg-indigo-200',
  // Form elements
  inputFocus: 'focus:ring-indigo-500 focus:border-indigo-500',
  checkboxChecked: 'text-indigo-500',
  toggleChecked: 'peer-checked:bg-indigo-500',
  toggleFocusRing: 'peer-focus:ring-indigo-500',
}
const vendorTheme: RoleThemeColors = {
  // Primary colors
  primary: 'purple-600',
  primaryHover: 'purple-700',
  primaryLight: 'purple-100',
  primaryDark: 'purple-700',
  // Background colors
  bgPrimary: 'bg-purple-600',
  bgPrimaryHover: 'hover:bg-purple-700',
  bgPrimaryLight: 'bg-purple-100',
  // Text colors
  textPrimary: 'text-purple-600',
  textPrimaryHover: 'hover:text-purple-700',
  // Border colors
  borderPrimary: 'border-purple-600',
  // Focus ring
  focusRing: 'focus:ring-purple-600',
  focusBorder: 'focus:border-purple-600',
  // Active/Selected states
  activeBackground: 'bg-purple-100',
  activeText: 'text-purple-700',
  // Button variants
  buttonPrimary: 'bg-purple-600 hover:bg-purple-700 text-white',
  buttonPrimaryHover: 'hover:bg-purple-700',
  buttonSecondary: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
  buttonSecondaryHover: 'hover:bg-purple-200',
  // Form elements
  inputFocus: 'focus:ring-purple-600 focus:border-purple-600',
  checkboxChecked: 'text-purple-600',
  toggleChecked: 'peer-checked:bg-purple-600',
  toggleFocusRing: 'peer-focus:ring-purple-600',
}

/**
 * Get theme colors based on user role
 */
export const getRoleTheme = (role: UserRole): RoleThemeColors => {
  return role === 'reader' ? readerTheme : vendorTheme
}

/**
 * Get the primary color name for a role (without Tailwind prefix)
 */
export const getRolePrimaryColor = (role: UserRole): string => {
  return role === 'reader' ? 'indigo' : 'purple'
}

/**
 * Helper to generate common class combinations
 */
export const getRoleClasses = (role: UserRole) => {
  const isVendor = role === 'vendor'
  const color = isVendor ? 'purple' : 'indigo'
  const base = isVendor ? '600' : '500'
  const hover = isVendor ? '700' : '600'
  const light = isVendor ? '100' : '100'
  const lightHover = isVendor ? '200' : '200'
  const focus = isVendor ? '600' : '500'
  const text = isVendor ? '700' : '600'
  const bgLight = isVendor ? '50' : '50'
  return {
    // Buttons
    primaryButton: `bg-${color}-${base} hover:bg-${color}-${hover} text-white focus:ring-${color}-${focus}`,
    secondaryButton: `bg-${color}-${light} text-${color}-${text} hover:bg-${color}-${lightHover}`,
    outlineButton: `border-${color}-${base} text-${color}-${base} hover:bg-${color}-${bgLight}`,
    // Form inputs
    input: `focus:border-${color}-${focus} focus:ring-${color}-${focus}`,
    checkbox: `text-${color}-${base} focus:ring-${color}-${focus}`,
    toggle: `peer-checked:bg-${color}-${base} peer-focus:ring-${color}-${isVendor ? '500' : '400'}`,
    // Navigation/Tabs
    activeTab: `bg-${color}-${light} text-${color}-${text}`,
    inactiveTab: `text-gray-700 hover:bg-gray-100`,
    // Links
    link: `text-${color}-${base} hover:text-${color}-${hover}`,
    // Badges/Tags
    badge: `bg-${color}-${light} text-${color}-${text}`,
    // Borders
    borderActive: `border-${color}-${base}`,
    borderFocus: `focus:border-${color}-${focus}`,
    // Text
    textPrimary: `text-${color}-${base}`,
    textHover: `hover:text-${color}-${hover}`,
    // Backgrounds
    bgLight: `bg-${color}-${bgLight}`,
    bgMedium: `bg-${color}-${light}`,
    bgDark: `bg-${color}-${base}`,
  }
}

/**
 * CSS custom properties for role-based theming (for use in style attributes or CSS)
 */
export const getRoleCSSVars = (role: UserRole): Record<string, string> => {
  const isReader = role === 'reader'
  return {
    '--role-primary-50': isReader ? '#eef2ff' : '#faf5ff',
    '--role-primary-100': isReader ? '#e0e7ff' : '#f3e8ff',
    '--role-primary-200': isReader ? '#c7d2fe' : '#e9d5ff',
    '--role-primary-300': isReader ? '#a5b4fc' : '#d8b4fe',
    '--role-primary-400': isReader ? '#818cf8' : '#c084fc',
    '--role-primary-500': isReader ? '#6366f1' : '#a855f7',
    '--role-primary-600': isReader ? '#4f46e5' : '#9333ea',
    '--role-primary-700': isReader ? '#4338ca' : '#7e22ce',
    '--role-primary-800': isReader ? '#3730a3' : '#6b21a8',
    '--role-primary-900': isReader ? '#312e81' : '#581c87',
  }
}
export default getRoleTheme
